import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { downloadTextFile } from '../../utils';
import { ButtonProps } from './button';
import { ProgressButton } from './progressButton';

export interface CsvExportPayload {
  content: string;
  filename: string;
}

interface ExportCsvButtonProps {
  /** Builds CSV content and download filename. Return null when there is nothing to export. */
  buildExport: () => CsvExportPayload | null | Promise<CsvExportPayload | null>;
  disabled?: boolean;
  onExportStart?: () => void;
  onExportEnd?: () => void;
  buttonText?: string;
  emptyMessage?: string;
  successMessage?: string;
  errorMessage?: string;
  'data-test'?: string;
  variant?: ButtonProps['variant'];
}

export function ExportCsvButton({
  buildExport,
  disabled = false,
  onExportStart,
  onExportEnd,
  buttonText = 'Export CSV',
  emptyMessage = 'Nothing to export.',
  successMessage = 'Export complete.',
  errorMessage = 'Failed to export.',
  'data-test': dataTest,
  variant = 'primary',
}: ExportCsvButtonProps) {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    onExportStart?.();

    try {
      const payload = await buildExport();
      if (!payload) {
        toast.info(emptyMessage);
        return;
      }

      downloadTextFile(payload.content, payload.filename);
      toast.success(successMessage);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error(errorMessage);
    } finally {
      setExporting(false);
      onExportEnd?.();
    }
  };

  return (
    <ProgressButton
      variant={variant}
      onClick={handleExport}
      disabled={disabled}
      isLoading={exporting}
      data-test={dataTest}
    >
      {buttonText}
    </ProgressButton>
  );
}
